import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, Button,
  Alert, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel, LinearProgress,
  InputAdornment, Tabs, Tab, Snackbar, Tooltip, Avatar, IconButton,
} from '@mui/material';
import {
  Search, CheckCircle, Warning, Cancel, Schedule, Edit,
  PersonAdd, ExpandMore,
} from '@mui/icons-material';
import { apiFetch } from '../../services/apiFetch';

interface ProbationPeriod {
  id: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  extendedEndDate?: string;
  status: 'active' | 'passed' | 'extended' | 'failed';
  decision?: 'confirm' | 'extend' | 'terminate';
  decisionDate?: string;
  managerComments?: string;
  hrComments?: string;
  checkinNotes?: string;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    employeeNumber?: string;
    department?: { name: string };
    position?: { title: string };
    profilePhotoUrl?: string;
  };
}

const daysUntil = (dateStr: string): number => {
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - now.getTime()) / 86400000);
};

const urgencyColor = (days: number) => {
  if (days < 0)  return '#d32f2f';
  if (days <= 7)  return '#f57c00';
  if (days <= 21) return '#fbc02d';
  return '#388e3c';
};

const STATUS_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  active:   { label: 'Active',    color: 'info' },
  passed:   { label: 'Passed ✓',  color: 'success' },
  extended: { label: 'Extended',  color: 'warning' },
  failed:   { label: 'Failed',    color: 'error' },
};

interface DecisionDialogProps {
  open: boolean;
  probation: ProbationPeriod | null;
  onClose: () => void;
  onSave: (id: string, payload: any) => Promise<void>;
}

const DecisionDialog: React.FC<DecisionDialogProps> = ({ open, probation, onClose, onSave }) => {
  const [decision, setDecision] = useState<'confirm' | 'extend' | 'terminate'>('confirm');
  const [hrComments, setHrComments] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && probation) {
      setDecision('confirm');
      setHrComments(probation.hrComments || '');
      setNewEndDate('');
    }
  }, [open, probation]);

  const handleSave = async () => {
    if (!probation) return;
    setSaving(true);
    try {
      if (decision === 'extend') {
        await onSave(probation.id + '/extend', { newEndDate, comments: hrComments });
      } else {
        await onSave(probation.id + '/decision', { decision, hrComments });
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!probation) return null;
  const empName = `${probation.employee.firstName} ${probation.employee.lastName}`;
  const effectiveEnd = probation.extendedEndDate || probation.endDate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Probation Decision — {empName}
        <Typography variant="body2" color="text.secondary">
          {probation.employee.position?.title} · {probation.employee.department?.name}
          &nbsp;· Ends {effectiveEnd}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Decision</InputLabel>
          <Select value={decision} onChange={(e) => setDecision(e.target.value as any)} label="Decision">
            <MenuItem value="confirm">✅ Confirm Employment</MenuItem>
            <MenuItem value="extend">⏳ Extend Probation</MenuItem>
            <MenuItem value="terminate">❌ Terminate</MenuItem>
          </Select>
        </FormControl>
        {decision === 'extend' && (
          <TextField
            label="New End Date" type="date" fullWidth sx={{ mb: 2 }}
            value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: effectiveEnd }}
          />
        )}
        <TextField
          label="HR Comments" multiline rows={3} fullWidth
          value={hrComments} onChange={(e) => setHrComments(e.target.value)}
          placeholder="Notes on decision, performance summary…"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={decision === 'terminate' ? 'error' : 'primary'}
          onClick={handleSave}
          disabled={saving || (decision === 'extend' && !newEndDate)}
        >
          {saving ? <CircularProgress size={18} /> : 'Save Decision'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ────────────────────────── Main component ────────────────────────────── */

const ProbationTracker: React.FC = () => {
  const [all, setAll]           = useState<ProbationPeriod[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [tab, setTab]           = useState(0);   // 0=active, 1=due soon, 2=all
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState<ProbationPeriod | null>(null);
  const [toast, setToast]       = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/performance/probation');
      if (!r.ok) throw new Error('Failed to load probation records');
      setAll(await r.json());
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDecisionSave = async (idPath: string, payload: any) => {
    const r = await apiFetch(`/api/performance/probation/${idPath}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error('Save failed');
    setToast('Decision saved ✓');
    fetchAll();
  };

  const displayList = useMemo(() => {
    let list = all;
    if (tab === 0) list = all.filter((p) => p.status === 'active');
    if (tab === 1) {
      list = all.filter((p) => {
        if (p.status !== 'active') return false;
        const eff = p.extendedEndDate || p.endDate;
        const d = daysUntil(eff);
        return d <= 30;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        `${p.employee.firstName} ${p.employee.lastName}`.toLowerCase().includes(q) ||
        p.employee.department?.name?.toLowerCase().includes(q) ||
        p.employee.position?.title?.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => {
      const aD = daysUntil(a.extendedEndDate || a.endDate);
      const bD = daysUntil(b.extendedEndDate || b.endDate);
      return aD - bD;
    });
  }, [all, tab, search]);

  const stats = useMemo(() => {
    const active = all.filter((p) => p.status === 'active');
    const overdue = active.filter((p) => daysUntil(p.extendedEndDate || p.endDate) < 0);
    const dueSoon = active.filter((p) => { const d = daysUntil(p.extendedEndDate || p.endDate); return d >= 0 && d <= 7; });
    const due30   = active.filter((p) => { const d = daysUntil(p.extendedEndDate || p.endDate); return d >= 0 && d <= 30; });
    const passed  = all.filter((p) => p.status === 'passed').length;
    return { total: active.length, overdue: overdue.length, dueSoon: dueSoon.length, due30: due30.length, passed };
  }, [all]);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Probation Tracker</Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor employee probation periods, record decisions, and track outcomes
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI cards */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ borderLeft: '4px solid #1976d2' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color="primary">{stats.total}</Typography>
              <Typography variant="caption">Active Probations</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ borderLeft: '4px solid #d32f2f' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color="error">{stats.overdue}</Typography>
              <Typography variant="caption">⚠️ Overdue — No Decision</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ borderLeft: '4px solid #f57c00' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">{stats.dueSoon}</Typography>
              <Typography variant="caption">Due in ≤7 days</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ borderLeft: '4px solid #388e3c' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color="success.main">{stats.passed}</Typography>
              <Typography variant="caption">Confirmed This Year</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs + Search */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary">
          <Tab label={`Active (${stats.total})`} />
          <Tab label={`Due in 30d (${stats.due30})`} />
          <Tab label="All Records" />
        </Tabs>
        <TextField
          size="small" placeholder="Search employee / dept…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 200 }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Department / Position</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Days Left</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Decision</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayList.map((p) => {
                  const effEnd = p.extendedEndDate || p.endDate;
                  const days   = daysUntil(effEnd);
                  const urgCol = urgencyColor(days);
                  const total  = Math.max(1, daysUntil(p.endDate) - daysUntil(p.startDate));
                  const elapsed= daysUntil(p.startDate) * -1;
                  const pct    = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
                  const empName = `${p.employee.firstName} ${p.employee.lastName}`;
                  const sc = STATUS_CHIP[p.status] || STATUS_CHIP.active;
                  return (
                    <TableRow key={p.id} hover
                      sx={{ bgcolor: days < 0 && p.status === 'active' ? '#fff8f8' : undefined }}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={p.employee.profilePhotoUrl} sx={{ width: 28, height: 28, fontSize: 12 }}>
                            {p.employee.firstName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{empName}</Typography>
                            {p.employee.employeeNumber && (
                              <Typography variant="caption" color="text.secondary">#{p.employee.employeeNumber}</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                          {p.employee.department?.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {p.employee.position?.title || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.startDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {effEnd}
                          {p.extendedEndDate && (
                            <Chip label="Extended" size="small" sx={{ ml: 0.5, fontSize: 9 }} color="warning" />
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: urgCol }}>
                          {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today!' : `${days}d`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <Tooltip title={`${pct}% through probation`}>
                          <LinearProgress
                            variant="determinate" value={pct}
                            color={pct < 50 ? 'primary' : pct < 80 ? 'warning' : 'success'}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={sc.label} size="small" color={sc.color} />
                      </TableCell>
                      <TableCell>
                        {p.decision ? (
                          <Typography variant="caption" color="text.secondary">
                            {p.decision === 'confirm' ? '✅ Confirmed' : p.decision === 'extend' ? '⏳ Extended' : '❌ Terminated'}
                            {p.decisionDate && ` · ${p.decisionDate}`}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">Pending</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {p.status === 'active' && (
                          <Tooltip title="Record decision">
                            <IconButton size="small" color="primary" onClick={() => setSelected(p)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {displayList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Box py={4}>
                        <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                        <Typography color="text.secondary">
                          {tab === 1 ? 'No probation reviews due in the next 30 days.' : 'No records found.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <DecisionDialog
        open={!!selected} probation={selected}
        onClose={() => setSelected(null)}
        onSave={handleDecisionSave}
      />

      <Snackbar
        open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ProbationTracker;
